import { useEffect, useRef, useState } from "react";
import { api } from "@/api/client";
import { READ_ONLY_TITLE, useReadOnly, useStore, usePhase } from "@/state/store";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useOffsiteConfig } from "@/engine/app/offsite-context";

export function ThemenSlide() {
  const { state, dispatch } = useStore();
  const phase = usePhase();
  const readOnly = useReadOnly();
  const { preparedTopics } = useOffsiteConfig();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  // „+"-Kasten: Zuruf aus dem Publikum direkt auf der Leinwand erfassen.
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [savingAdd, setSavingAdd] = useState(false);

  // Refresh topics from backend on mount + every 4s while editor admin
  // could be making changes simultaneously.
  const loadedRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.topics.list(state.auth.token);
        if (!cancelled) {
          dispatch({ type: "topics/set", topics: r.topics });
          loadedRef.current = true;
        }
      } catch {
        /* noop */
      }
    };
    load();
    const id = setInterval(load, 4000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [state.auth.token, dispatch]);

  const topics = state.workshop.topics;
  // Erfassen/Entfernen nur vor der Abstimmung — sobald Stimmen laufen,
  // bleibt die Liste stabil.
  const canEdit = !readOnly && phase !== "voting";

  // Die vorgegebenen Themen sind fest verdrahtet (offsite.config.ts):
  // fehlen sie in der Liste (frisches System, Reset), legt die Leinwand
  // sie selbst wieder an. Ein ✕ bekommen sie nicht.
  const preparedTitles = (preparedTopics ?? []).map((p) => p.short);
  const seedingRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!canEdit || preparedTitles.length === 0) return;
    // Erst handeln, wenn die Liste wirklich vom Server geladen wurde —
    // sonst würde ein leerer Anfangszustand Duplikate anlegen.
    if (!loadedRef.current) return;
    const existing = new Set(topics.map((t) => t.title));
    for (const title of preparedTitles) {
      if (existing.has(title) || seedingRef.current.has(title)) continue;
      seedingRef.current.add(title);
      api.admin
        .addTopic(state.auth.token, title)
        .then(async () => {
          const r = await api.topics.list(state.auth.token);
          dispatch({ type: "topics/set", topics: r.topics });
        })
        .catch((err) => console.error(err))
        .finally(() => seedingRef.current.delete(title));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topics, canEdit]);

  const reloadTopics = async () => {
    const r = await api.topics.list(state.auth.token);
    dispatch({ type: "topics/set", topics: r.topics });
  };

  const addTopic = async () => {
    const title = draft.trim();
    if (!title || savingAdd) return;
    setSavingAdd(true);
    setError(null);
    try {
      await api.admin.addTopic(state.auth.token, title);
      await reloadTopics();
      setDraft("");
      setAdding(false);
    } catch (err) {
      console.error(err);
      setError("Konnte Thema nicht anlegen.");
    } finally {
      setSavingAdd(false);
    }
  };

  const removeTopic = async (topicId: string) => {
    setError(null);
    try {
      await api.admin.deleteTopic(state.auth.token, topicId);
      await reloadTopics();
    } catch (err) {
      console.error(err);
      setError("Konnte Thema nicht entfernen.");
    }
  };

  const startVoting = async () => {
    setBusy(true);
    setError(null);
    try {
      // 2-Min-Timer gleich mitsetzen — Tally-Board zeigt Countdown,
      // Teilnehmer-Handys bekommen Pill.
      const p = await api.phase.set(state.auth.token, "voting", 2 * 60);
      dispatch({ type: "phase/set", phase: p });
    } catch (err) {
      console.error(err);
      setError("Konnte Abstimmung nicht freischalten.");
    } finally {
      setBusy(false);
    }
  };

  const doResetVoting = async () => {
    setConfirmReset(false);
    setBusy(true);
    setError(null);
    try {
      await api.votes.reset(state.auth.token);
      const p = await api.phase.set(state.auth.token, "voting", 2 * 60);
      dispatch({ type: "phase/set", phase: p });
    } catch (err) {
      console.error(err);
      setError("Konnte Abstimmung nicht zurücksetzen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="deck-root themen-slide" style={{ background: "var(--svo-dunkelblau)" }}>
      <div className="deck-bg" aria-hidden="true" />
      <div
        className="float"
        aria-hidden="true"
        style={{
          width: 460,
          height: 460,
          top: "-100px",
          right: "-140px",
          background: "var(--svo-blau)",
          opacity: 0.32,
        }}
      />
      <div
        className="float"
        aria-hidden="true"
        style={{
          width: 360,
          height: 360,
          bottom: "-120px",
          left: "8%",
          background: "var(--svo-gruen)",
          opacity: 0.18,
          animationDelay: "4s",
        }}
      />

      <header className="deck-head">
        <div
          className="deck-eyebrow rise rise-1"
          style={{ color: "var(--svo-blau)" }}
        >
          Workshop · Block 1
        </div>
        <h1
          className="deck-title rise rise-2"
          style={{ color: "var(--svo-weiss)" }}
        >
          Themen <span className="accent">für heute</span>
        </h1>
        <p
          className="deck-sub rise rise-3"
          style={{ color: "rgba(255,255,255,0.78)" }}
        >
          Zwei Themen bringen wir mit — eure Vorschläge kommen direkt dazu.
          Danach stimmt ihr ab.
        </p>
      </header>

      <div className="themen-grid">
        {topics.length === 0 && (
          <div className="themen-empty rise rise-4">
            Noch keine Themen im Backend angelegt — öffne /admin → Themen.
          </div>
        )}
        {topics.map((t, i) => (
          <article
            key={t.id}
            className={`themen-card rise rise-${Math.min(i + 4, 6)}`}
            style={{ position: "relative" }}
          >
            <div className="themen-card-num">{String(i + 1).padStart(2, "0")}</div>
            <h2 className="themen-card-title">{t.title}</h2>
            {canEdit && !preparedTitles.includes(t.title) && (
              <button
                type="button"
                className="themen-card-remove"
                onClick={() => removeTopic(t.id)}
                title="Thema entfernen"
                aria-label={`Thema „${t.title}“ entfernen`}
              >
                ✕
              </button>
            )}
          </article>
        ))}
        {canEdit && !adding && (
          <button
            type="button"
            className="themen-card themen-card-plus rise rise-6"
            onClick={() => setAdding(true)}
          >
            <span className="plus-sign" aria-hidden="true">+</span>
            <span className="plus-label">Thema aus dem Publikum aufnehmen</span>
          </button>
        )}
        {canEdit && adding && (
          <article className="themen-card rise rise-6">
            <div className="themen-card-num">
              {String(topics.length + 1).padStart(2, "0")}
            </div>
            <input
              className="themen-card-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Thema eintippen …"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") addTopic();
                if (e.key === "Escape") {
                  setAdding(false);
                  setDraft("");
                }
              }}
            />
            <div className="themen-card-actions">
              <button
                type="button"
                className="btn btn-success"
                onClick={addTopic}
                disabled={!draft.trim() || savingAdd}
              >
                {savingAdd ? "…" : "✓ Aufnehmen"}
              </button>
              <button
                type="button"
                className="btn btn-ghost-on-dark"
                onClick={() => {
                  setAdding(false);
                  setDraft("");
                }}
                disabled={savingAdd}
              >
                Abbrechen
              </button>
            </div>
          </article>
        )}
      </div>

      <footer className="themen-foot rise rise-6">
        <div className="themen-foot-info">
          {topics.length > 0
            ? `${topics.length} ${topics.length === 1 ? "Thema" : "Themen"} bereit`
            : "Themen werden geladen …"}
        </div>
        <div className="themen-foot-actions">
          {error && (
            <span style={{ color: "var(--svo-error)", fontSize: 14 }}>
              {error}
            </span>
          )}
          {phase === "voting" ? (
            <>
              <span className="themen-status-pill">
                <span className="live-dot" /> Abstimmung läuft auf den Handys
              </span>
              <button
                type="button"
                className={`btn btn-ghost${readOnly ? " is-readonly" : ""}`}
                onClick={() => setConfirmReset(true)}
                disabled={busy || readOnly}
                title={
                  readOnly
                    ? READ_ONLY_TITLE
                    : "Stimmen löschen + neuen 2-min-Timer starten"
                }
              >
                {busy ? "…" : "↺ Abstimmung zurücksetzen"}
              </button>
            </>
          ) : (
            <button
              type="button"
              className={`btn btn-success themen-cta${readOnly ? " is-readonly" : ""}`}
              onClick={startVoting}
              disabled={busy || topics.length === 0 || readOnly}
              title={readOnly ? READ_ONLY_TITLE : undefined}
            >
              {busy ? "…" : "→ Abstimmen freischalten"}
            </button>
          )}
        </div>
      </footer>

      <ConfirmDialog
        open={confirmReset}
        title="Abstimmung zurücksetzen?"
        message={
          "Alle Stimmen werden gelöscht und ein neuer 2-Minuten-Timer startet. " +
          "Alle Teilnehmer-Handys wechseln zurück zur Auswahl-Ansicht."
        }
        confirmLabel="Ja, zurücksetzen"
        cancelLabel="Abbrechen"
        variant="danger"
        onConfirm={doResetVoting}
        onCancel={() => setConfirmReset(false)}
        busy={busy}
      />
    </div>
  );
}
